import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { PhoneField } from "@/components/ui/PhoneField";
import { DEFAULT_PHONE_DIAL_CODE } from "@/constants/phoneDialCodes";
import { authService } from "@/services/auth.service";
import { useAuthStore } from "@/store/authStore";

/**
 * Page d'inscription du portail client.
 *
 * Crée un compte `CUSTOMER` + fiche `Customer` liée via `POST /api/auth/register-client`.
 */
export function ClientRegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState(DEFAULT_PHONE_DIAL_CODE);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!lastName.trim()) {
      setError("Veuillez renseigner votre nom.");
      return;
    }
    if (!email || !password) {
      setError("Email et mot de passe sont requis.");
      return;
    }
    if (!termsAccepted) {
      setError("Vous devez accepter les conditions générales.");
      return;
    }

    setSubmitting(true);
    try {
      const { user, token } = await authService.registerClient({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim(),
        phoneDialCode,
        phone: phone.trim() || undefined,
        email: email.trim(),
        password,
        termsAccepted: true,
      });
      setAuth(user, token, true);
      navigate("/client", { replace: true });
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        "Une erreur est survenue lors de la création de votre compte.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.default",
        p: 2,
      }}
    >
      <Container maxWidth="xs" sx={{ width: "100%" }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 3,
            boxShadow: 1,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Créer votre compte
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inscrivez-vous pour commander vos repas Défi Gourmand.
          </Typography>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Prénom"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
            <TextField
              fullWidth
              label="Nom"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
            />
            <PhoneField
              dialCode={phoneDialCode}
              phone={phone}
              onDialCodeChange={setPhoneDialCode}
              onPhoneChange={setPhone}
              size="medium"
            />
            <TextField
              fullWidth
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              label="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              helperText="8 caractères minimum, avec 1 majuscule, 1 minuscule et 1 chiffre."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label="afficher le mot de passe"
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  color="secondary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  J’accepte les conditions générales et la politique de confidentialité.
                </Typography>
              }
            />

            <Button
              type="submit"
              variant="contained"
              color="secondary"
              size="large"
              fullWidth
              disabled={submitting}
            >
              {submitting ? "Création…" : "Créer mon compte"}
            </Button>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <MuiLink component={RouterLink} to="/login" sx={{ fontSize: 14 }}>
                J’ai déjà un compte
              </MuiLink>
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
